import React, { JSXElementConstructor } from "react";

import FullScreenIcon from "@uui/Expand06";
import NextHighlightIcon from "@uui/FlipForward";
import MaximizeIcon from "@uui/Maximize01";
import MinimizeIcon from "@uui/Minimize01";
import SettingsIcon from "@uui/Settings01";
import PreviousIcon from "@uui/SkipBack";
import NextIcon from "@uui/SkipForward";
import UnmutedIcon from "@uui/VolumeMax";
import MutedIcon from "@uui/VolumeX";

import type { IconButtonProps, SvgIconProps } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import styled from "@mui/material/styles/styled";

import type { VideoPlayerAction } from "@app/analytics/types";
import {
  PauseIcon,
  PlayFilled,
  SkipBackward10Icon,
  SkipForward10Icon,
} from "@app/common/assets/icons";

import LoadSpinner from "@rui/LoadSpinner";
import Stack from "@rui/Stack";

import ControlTooltip from "../ControlTooltip";

const ICONS: {
  [key in VideoPlayerAction]: JSXElementConstructor<SvgIconProps>;
} = {
  nextHighlight: NextHighlightIcon,
  settings: SettingsIcon,
  fullScreen: FullScreenIcon,
  mute: UnmutedIcon,
  unmute: MutedIcon,
  pause: PauseIcon,
  play: PlayFilled,
  minimize: MinimizeIcon,
  maximize: MaximizeIcon,
  skipBackward10: SkipBackward10Icon,
  skipForward10: SkipForward10Icon,
  previous: PreviousIcon,
  next: NextIcon,
};

export interface VideoIconButtonProps extends IconButtonProps {
  icon?: keyof typeof ICONS;
  tooltip?: string;
  shade?: "light" | "dark";
  loading?: boolean;
}

const VideoIconButton = styled(
  ({
    icon,
    tooltip,
    children,
    shade: _s,
    loading,
    ...props
  }: VideoIconButtonProps) => {
    const Icon = typeof icon === "string" ? ICONS[icon] : undefined;
    if (tooltip) {
      return (
        <ControlTooltip title={tooltip}>
          {loading ? (
            <Stack variant="center" height="24px" width="24px">
              <LoadSpinner size={16} color="#FFF" />
            </Stack>
          ) : (
            <IconButton {...props}>{Icon ? <Icon /> : children}</IconButton>
          )}
        </ControlTooltip>
      );
    }
    return <IconButton {...props}>{Icon ? <Icon /> : children}</IconButton>;
  }
)(({ theme, shade }) => ({
  width: "24px",
  height: "24px",
  color: shade === "light" ? theme.palette.shades.neutral[500] : "#fff",
  backgroundColor: shade === "light" ? "#fafafa" : "transparent",
  borderRadius: "8px",
  transition: "background-color 250ms",
  "& svg": { width: "20px", height: "20px" },
  "&:hover": {
    backgroundColor:
      shade === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.3)",
  },
  "&:disabled": {
    color: theme.palette.shades.neutral[300],
  },
}));

export default VideoIconButton;
